import { Box, Button, Chip, Popover, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useState } from "react";
import {
  getSourceMetadata,
  SOURCE_PROPERTIES_HELP,
  type SourceMetadataEntry,
} from "src/features/library/record/sourceMetadata";
import HelpTooltipIcon from "src/reusable/components/HelpTooltipIcon";

/** Identity keys shown as chips ahead of the full list, with their labels. */
const HIGHLIGHTS: ReadonlyArray<{
  key: string;
  format: (value: string) => string;
}> = [
  { key: "item_number", format: (v) => v },
  { key: "revision", format: (v) => `Rev ${v}` },
  { key: "state", format: (v) => v },
];

/** Every source metadata entry as a two-column key/value list. */
const SourceMetadataList = ({
  entries,
}: {
  entries: SourceMetadataEntry[];
}) => (
  <Box
    component="dl"
    sx={{
      display: "grid",
      gridTemplateColumns: "max-content minmax(0, 1fr)",
      columnGap: 2,
      rowGap: 0.75,
      m: 0,
    }}
  >
    {entries.map(({ key, label, value }) => (
      <Box key={key} sx={{ display: "contents" }}>
        <Typography
          component="dt"
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="caption"
          sx={{ m: 0, color: "text.primary", overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * A compact summary of a record's source metadata for list surfaces: the
 * identity fields (item number, revision, state) as chips, plus a button that
 * opens every entry in a popover. Renders nothing when the record has none.
 *
 * Both host surfaces navigate on click, so only the button and the popover
 * (whose portal still bubbles React events to them) stop propagation; the
 * chips and the space around them open the document as before.
 */
export const SourceMetadataSummary = ({
  data,
  recordId,
}: {
  data: { source_metadata?: unknown } | null | undefined;
  recordId: number;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const entries = getSourceMetadata(data);
  if (entries.length === 0) return null;

  const byKey = new Map(entries.map((entry) => [entry.key, entry]));
  const chips = HIGHLIGHTS.flatMap(({ key, format }) => {
    const entry = byKey.get(key);
    return entry
      ? [{ key, label: entry.label, text: format(entry.value) }]
      : [];
  });

  return (
    <Stack
      direction="row"
      spacing={0.5}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      data-testid={`sdr-source-metadata-${recordId}`}
    >
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.text}
          title={chip.label}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            maxWidth: 160,
            fontSize: "0.75rem",
            color: grey[700],
          }}
        />
      ))}
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-haspopup="dialog"
        data-testid={`sdr-source-metadata-open-${recordId}`}
        sx={{ minWidth: 0, px: 0.5, py: 0, textTransform: "none" }}
      >
        Source properties ({entries.length})
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { p: 2, maxWidth: 420, maxHeight: 480, overflowY: "auto" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Source properties
          </Typography>
          <HelpTooltipIcon
            label="About Source properties"
            help={SOURCE_PROPERTIES_HELP}
            testId={`sdr-source-metadata-help-${recordId}`}
          />
        </Box>
        <SourceMetadataList entries={entries} />
      </Popover>
    </Stack>
  );
};
